import {delay} from '@yourcoach/async-utils';

import type {Destructible} from '../../../../utils/store';
import type {RemoteFileStorageService} from '../../../system/remoteFileStorage/RemoteFileStorage.service';

import {envManager} from '../../../../utils/env';
import {fetchMedia} from './fetchMedia';

export class FetchMediaRequest implements Destructible {
  private LOAD_MAX_ATTEMPTS = 4;
  private loadAttempts = 0;
  private RETRY_LOAD_INTERVAL = 1000 * 5; // 5 seconds

  constructor(
    private readonly remoteFileStorageService: RemoteFileStorageService,
  ) {}

  destruct() {
    this.loadAttempts = 0;
  }

  async exec(url: string): Promise<Response> {
    const signedUrl = this.remoteFileStorageService.url.sign(url);

    let response: Response;

    try {
      response = await fetchMedia(signedUrl);
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        // Most likely CORS issue
        const message = [
          'Failed to fetch media (most likely CORS)!',
          `Url: ${signedUrl}`,
        ].join(' | ');

        throw new Error(message);
      }

      throw error;
    }

    /**
     * @see https://us-east-1.console.aws.amazon.com/cloudfront/v3/home?region=us-east-1#/functions/request-library-local
     */
    const isMediaAccessDeniedResponse =
      response.url.includes('yourcoach.health') &&
      response.url.includes('access-denied');

    const isFailedToLoad = !response.ok || isMediaAccessDeniedResponse;

    /**
     * we retry to load the media only on core since it has media access check
     */
    const shouldRetryToLoadMedia =
      envManager.isCore &&
      response.ok &&
      isMediaAccessDeniedResponse &&
      this.loadAttempts < this.LOAD_MAX_ATTEMPTS;

    if (shouldRetryToLoadMedia) {
      this.loadAttempts += 1;

      await delay(this.RETRY_LOAD_INTERVAL);

      return this.exec(url);
    }

    if (isFailedToLoad) {
      const message = [
        'Failed to load media!',
        `Url: ${url}`,
        `Response url: ${response.url}`,
        `Status: ${response.status}`,
        `Status text: ${response.statusText}`,
        `Is redirected: ${response.redirected}`,
      ].join(' | ');

      throw new Error(message);
    }

    this.loadAttempts = 0;

    return response;
  }
}
